import React, { useEffect, useMemo, useState } from "react";
import {
  addToQueue,
  clearPdfExtractScan,
  getFDDQueueByType,
  getFileBlob,
  getPdfExtractScan,
  setItemStatus,
  setPdfExtractScan,
} from "../../store/fddStore";
import type { DetectedTable, PdfExtractScan } from "../../types";
import { ensureSharePointRefs } from "../../runtime/resolveQueuedFile";
import { getToken } from "../../../utils/tokenHelper";
import { apiFetch } from "../../../api/fddClient";

const ACCEPTED_EXT = /\.(pdf|png|jpe?g|gif|webp)$/i;

type Props = {
  projectId: string;
  projectName?: string;
  queueVersion: number;
  onQueueChanged: () => void;
};

type Phase = "idle" | "analyzing" | "review" | "building" | "done";

async function fetchSharePointFile(driveId: string, itemId: string, fileName: string): Promise<File> {
  const token = await getToken();
  const url = `https://graph.microsoft.com/v1.0/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/content`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    throw new Error(`Failed to download "${fileName}" from SharePoint (HTTP ${res.status})`);
  }
  const blob = await res.blob();
  return new File([blob], fileName, { type: blob.type || "application/pdf" });
}

// Excel sheet names cap at 31 characters. We mirror that on the UI title so
// the input box and the eventual sheet name stay in sync.
const TITLE_MAX = 31;

// Matches SCAN_SESSION_TTL_MS in server/pdf-extract/route.ts. Past this the
// server has swept the session, so restored results could no longer build.
const SCAN_TTL_MS = 30 * 60 * 1000;

const PdfExtractPanel: React.FC<Props> = ({ projectId, projectName, queueVersion, onQueueChanged }) => {
  const queue = useMemo(
    () => getFDDQueueByType(projectId, "pdf-extract"),
    [projectId, queueVersion],
  );

  // Restore this project's last scan — the panel unmounts on every tool
  // switch. Dropped once the server's scan session would have expired.
  const [restored] = useState<PdfExtractScan | undefined>(() => {
    const s = getPdfExtractScan(projectId);
    if (s && Date.now() - s.scannedAt < SCAN_TTL_MS) return s;
    clearPdfExtractScan(projectId);
    return undefined;
  });

  const [phase, setPhase] = useState<Phase>(
    restored ? (restored.downloaded ? "done" : "review") : "idle",
  );
  const [error, setError] = useState<string | null>(null);
  const [downloaded, setDownloaded] = useState(restored?.downloaded ?? false);

  // Tables returned by /scan. We keep them as-is and track edits + selection separately.
  const [tables, setTables] = useState<DetectedTable[]>(restored?.tables ?? []);
  // Per-table title overrides keyed by table id (in case user renames).
  const [titles, setTitles] = useState<Record<string, string>>(restored?.titles ?? {});
  // Set of table ids the user wants to include in the build.
  const [selected, setSelected] = useState<Set<string>>(new Set(restored?.selected ?? []));
  // Server-side session id from /scan. /build references it to find the
  // uploaded file bytes for per-table extraction.
  const [sessionId, setSessionId] = useState<string | null>(restored?.sessionId ?? null);
  const [scannedAt, setScannedAt] = useState(restored?.scannedAt ?? 0);

  // Keep the store in step with renames / selection so a tool switch keeps them.
  useEffect(() => {
    if (phase !== "review" && phase !== "done") return;
    setPdfExtractScan(projectId, {
      sessionId, tables, titles, selected: Array.from(selected), downloaded, scannedAt,
    });
  }, [projectId, phase, sessionId, tables, titles, selected, downloaded, scannedAt]);
  // Index into `tables` currently shown at full size in the flip-through viewer.
  // null = viewer closed.
  const [viewerIdx, setViewerIdx] = useState<number | null>(null);

  const candidateCount = queue.filter((q) => q.status !== "error").length;
  const canAnalyze = candidateCount > 0 && phase === "idle";

  /**
   * Add a pasted clipboard snip to this project's pdf-extract queue. Same
   * shape FDDToolbox uses for drag-drop / file-picker uploads, so downstream
   * code can't tell where the file came from. (Drag-drop and file picking
   * stay on the toolbox; this panel only owns the paste path.)
   */
  const queueLocalFiles = (files: File[]) => {
    let queued = 0;
    for (const f of files) {
      const looksValid =
        ACCEPTED_EXT.test(f.name) ||
        f.type.startsWith("image/") ||
        f.type === "application/pdf";
      if (!looksValid) continue;
      addToQueue(
        projectId,
        {
          fileId: f.name,
          fileName: f.name,
          fileWebUrl: "",
          fileSize: f.size,
          source: "local",
          analysisType: "pdf-extract",
          projectId,
        },
        f,
      );
      queued++;
    }
    if (queued > 0) onQueueChanged();
  };

  // Paste listener — captures image data from the system clipboard (e.g.
  // a Windows Snipping Tool clip) and queues it as a synthetic image file.
  // Only active during the idle phase so it doesn't intercept paste events
  // while the user is editing table titles in the review phase.
  useEffect(() => {
    if (phase !== "idle") return;
    const onPaste = (e: ClipboardEvent) => {
      // Don't hijack paste when the user is typing in a text input.
      const active = document.activeElement;
      if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA")) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (const item of Array.from(items)) {
        if (item.kind !== "file" || !item.type.startsWith("image/")) continue;
        const blob = item.getAsFile();
        if (!blob) continue;
        const ext = (blob.type.split("/")[1] || "png").replace("jpeg", "jpg");
        const ts = new Date()
          .toISOString()
          .replace(/[:.]/g, "-")
          .replace("T", "_")
          .slice(0, 19);
        files.push(new File([blob], `snip_${ts}.${ext}`, { type: blob.type }));
      }
      if (files.length === 0) return;
      e.preventDefault();
      queueLocalFiles(files);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [phase, projectId]);

  const handleAnalyze = async () => {
    const runnable = queue.filter((q) => q.status !== "error");
    if (!runnable.length) {
      setError("Queue is empty. Add at least one PDF or image.");
      return;
    }
    setPhase("analyzing");
    setError(null);
    setDownloaded(false);
    clearPdfExtractScan(projectId);
    setTables([]);
    setTitles({});
    setSelected(new Set());

    const formData = new FormData();
    if (projectName) formData.append("projectName", projectName);

    const itemsForStatus: { id: string }[] = [];

    try {
      for (const queuedItem of runnable) {
        setItemStatus(projectId, queuedItem.id, "in_progress");
        itemsForStatus.push({ id: queuedItem.id });
        const item = await ensureSharePointRefs(queuedItem, projectId);
        const localFile = getFileBlob(item.id);
        let file: File;
        if (localFile) {
          file = localFile;
        } else if (item.driveId && item.fileId) {
          file = await fetchSharePointFile(item.driveId, item.fileId, item.fileName);
        } else {
          throw new Error(
            `"${item.fileName}" was queued for analysis but couldn't be found — not in this browser session, no SharePoint refs on the queue item, and a search of the project's VDR didn't find it by name. Re-queue from the FDD SharePoint picker.`,
          );
        }
        formData.append("files", file, item.fileName);
      }
      onQueueChanged();

      const res = await apiFetch("/api/extract-tables/scan", { method: "POST", body: formData });
      if (!res.ok) {
        let message = `Detection failed (HTTP ${res.status})`;
        try {
          const errBody = await res.json();
          if (errBody?.error) message = errBody.error;
        } catch { /* ignore */ }
        throw new Error(message);
      }
      const data = await res.json();
      const detected: DetectedTable[] = Array.isArray(data?.tables) ? data.tables : [];
      setTables(detected);
      setSessionId(typeof data?.sessionId === "string" ? data.sessionId : null);
      // Default: every table selected, with its Claude-suggested title.
      const t: Record<string, string> = {};
      const s = new Set<string>();
      for (const tbl of detected) {
        t[tbl.id] = tbl.title.substring(0, TITLE_MAX);
        s.add(tbl.id);
      }
      setTitles(t);
      setSelected(s);
      const now = Date.now();
      setScannedAt(now);
      // Written here as well as by the effect, so a result that lands after
      // the user has switched tools is still there when they come back.
      if (detected.length > 0) {
        setPdfExtractScan(projectId, {
          sessionId: typeof data?.sessionId === "string" ? data.sessionId : null,
          tables: detected, titles: t, selected: Array.from(s), downloaded: false, scannedAt: now,
        });
      }

      const completedAt = new Date().toISOString();
      for (const item of itemsForStatus) {
        setItemStatus(projectId, item.id, "completed", { completedAt });
      }
      onQueueChanged();
      setPhase(detected.length > 0 ? "review" : "idle");
      if (detected.length === 0) setError("Claude didn't find any tables in the uploaded file(s).");
    } catch (err: any) {
      const message = err?.message || "Detection failed";
      setError(message);
      for (const item of itemsForStatus) {
        setItemStatus(projectId, item.id, "error", { error: message });
      }
      onQueueChanged();
      setPhase("idle");
    }
  };

  const handleBuild = async () => {
    // Metadata only. `snipImageBase64` is deliberately not spread in: the
    // server already holds every snip in the scan session and looks it up by
    // id. Posting the images back made this request megabytes wide, which
    // Front Door's WAF rejects with a bare 403 once the body passes 128KB.
    const picks = tables
      .filter((t) => selected.has(t.id))
      .map(({ snipImageBase64, ...t }) => ({
        ...t,
        title: (titles[t.id] || t.title).substring(0, TITLE_MAX),
      }));
    if (picks.length === 0) {
      setError("Select at least one table to include in the workbook.");
      return;
    }
    if (!sessionId) {
      setError("Scan session missing. Re-upload and detect tables.");
      return;
    }
    setPhase("building");
    setError(null);
    try {
      const res = await apiFetch("/api/extract-tables/build", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, projectName, tables: picks }),
      });
      if (!res.ok) {
        let message = `Build failed (HTTP ${res.status})`;
        try {
          const errBody = await res.json();
          if (errBody?.error) message = errBody.error;
        } catch { /* ignore */ }
        // 400 here means the scan session is gone (expired, restarted
        // worker, or not ours) — the tables can't be built, so drop them.
        if (res.status === 400) {
          handleStartOver();
          setError(message);
          return;
        }
        throw new Error(message);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "extracted-tables.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setDownloaded(true);
      setPhase("done");
    } catch (err: any) {
      setError(err?.message || "Build failed");
      setPhase("review");
    }
  };

  const handleStartOver = () => {
    clearPdfExtractScan(projectId);
    setPhase("idle");
    setTables([]);
    setTitles({});
    setSelected(new Set());
    setSessionId(null);
    setDownloaded(false);
    setError(null);
  };

  // "Remove" / "Clear All" on the queue drop the stored scan (fddStore);
  // follow suit on screen so the stale tables can't be built or re-saved.
  useEffect(() => {
    if ((phase === "review" || phase === "done") && !getPdfExtractScan(projectId)) handleStartOver();
  }, [queueVersion]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const selectAll = () => setSelected(new Set(tables.map((t) => t.id)));
  const selectNone = () => setSelected(new Set());

  return (
    <section
      style={{
        border: "1px solid var(--theme-border)",
        borderRadius: 10,
        background: "var(--theme-tint-3)",
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ fontWeight: 700, color: "var(--theme-ink)", fontSize: 13 }}>
        PDF Extraction
      </div>

      {phase === "idle" || phase === "analyzing" ? (
        <button
          onClick={handleAnalyze}
          disabled={!canAnalyze}
          style={{
            padding: 12,
            borderRadius: 10,
            border: "none",
            background: !canAnalyze ? "#9ca3af" : "var(--theme-accent)",
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            cursor: !canAnalyze ? "not-allowed" : "pointer",
          }}
        >
          {phase === "analyzing"
            ? `Detecting tables across ${candidateCount} file${candidateCount === 1 ? "" : "s"}…`
            : `Detect tables (${candidateCount})`}
        </button>
      ) : null}

      {phase === "review" || phase === "building" || phase === "done" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: "#374151" }}>
            <span style={{ fontWeight: 600 }}>
              {tables.length} table{tables.length === 1 ? "" : "s"} detected — {selected.size} selected
            </span>
            <button onClick={selectAll} style={linkBtn}>Select all</button>
            <button onClick={selectNone} style={linkBtn}>Select none</button>
            <span style={{ flex: 1 }} />
            <button onClick={handleStartOver} style={linkBtn}>Start over</button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
            {tables.map((t, i) => {
              const isSel = selected.has(t.id);
              const title = titles[t.id] ?? t.title;
              const remaining = TITLE_MAX - title.length;
              return (
                <div
                  key={t.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr auto",
                    gap: 12,
                    padding: 10,
                    background: isSel ? "#ffffff" : "#f3f4f6",
                    border: `1px solid ${isSel ? "var(--theme-accent)" : "#d1d5db"}`,
                    borderRadius: 8,
                    alignItems: "center",
                  }}
                >
                  <button
                    onClick={() => setViewerIdx(i)}
                    title="Click to view full size"
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 6,
                      padding: 0,
                      background: "#fff",
                      cursor: "pointer",
                      overflow: "hidden",
                      height: 90,
                    }}
                  >
                    {t.snipImageBase64 ? (
                      <img
                        src={`data:image/png;base64,${t.snipImageBase64}`}
                        alt={`Snip of ${t.title}`}
                        style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#6b7280" }}>
                        (no snip)
                      </div>
                    )}
                  </button>

                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <input
                      value={title}
                      maxLength={TITLE_MAX}
                      onChange={(e) => setTitles((prev) => ({ ...prev, [t.id]: e.target.value }))}
                      placeholder="Table name (sheet name)"
                      style={{
                        padding: "6px 8px",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    />
                    <div style={{ fontSize: 11, color: "#6b7280", display: "flex", gap: 10 }}>
                      <span>{t.sourceFile} · page {t.pageNum}{t.pageEnd !== t.pageNum ? `–${t.pageEnd}` : ""}</span>
                      <span style={{ textTransform: "capitalize" }}>
                        {t.tableType === "income_statement" ? "Income statement"
                          : t.tableType === "balance_sheet" ? "Balance sheet"
                          : "Other"}
                      </span>
                      <span style={{ color: remaining < 5 ? "#dc2626" : "#9ca3af", marginLeft: "auto" }}>
                        {remaining} char{remaining === 1 ? "" : "s"} left
                      </span>
                    </div>
                  </div>

                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggle(t.id)}
                      style={{ width: 18, height: 18, cursor: "pointer" }}
                    />
                    Include
                  </label>
                </div>
              );
            })}
          </div>

          <button
            onClick={handleBuild}
            disabled={phase === "building" || selected.size === 0}
            style={{
              padding: 12,
              borderRadius: 10,
              border: "none",
              background: phase === "building" || selected.size === 0 ? "#9ca3af" : "var(--theme-accent)",
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              cursor: phase === "building" || selected.size === 0 ? "not-allowed" : "pointer",
            }}
          >
            {phase === "building"
              ? "Building workbook…"
              : `Build Excel (${selected.size} table${selected.size === 1 ? "" : "s"})`}
          </button>
        </div>
      ) : null}

      {error && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fca5a5",
            borderRadius: 8,
            padding: 10,
            color: "#dc2626",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {downloaded && !error && (
        <div
          style={{
            background: "var(--theme-tint-9)",
            border: "1px solid var(--theme-accent)",
            borderRadius: 8,
            padding: 10,
            color: "var(--theme-action)",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Excel file downloaded — check your Downloads folder.
        </div>
      )}

      {viewerIdx !== null && tables[viewerIdx] && (
        <SnipViewer
          tables={tables}
          idx={viewerIdx}
          titles={titles}
          onClose={() => setViewerIdx(null)}
          onNav={(next) => setViewerIdx(next)}
        />
      )}
    </section>
  );
};

const linkBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#2563eb",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  padding: "2px 6px",
};

/**
 * Modal flip-through viewer. Shows the current table's snip at full size with
 * prev / next nav, the editable-mirrored title, and source/page metadata so
 * the user can verify what Claude saw before locking in the selection.
 */
const SnipViewer: React.FC<{
  tables: DetectedTable[];
  idx: number;
  titles: Record<string, string>;
  onClose: () => void;
  onNav: (nextIdx: number) => void;
}> = ({ tables, idx, titles, onClose, onNav }) => {
  const t = tables[idx];
  if (!t) return null;
  const prev = () => onNav(idx === 0 ? tables.length - 1 : idx - 1);
  const next = () => onNav(idx === tables.length - 1 ? 0 : idx + 1);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.7)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 10,
          padding: 16,
          maxWidth: "92vw",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>
            {titles[t.id] ?? t.title}
          </div>
          <div style={{ fontSize: 11, color: "#6b7280" }}>
            ({idx + 1} / {tables.length}) · {t.sourceFile} · page {t.pageNum}{t.pageEnd !== t.pageNum ? `–${t.pageEnd}` : ""}
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={prev} style={navBtn} title="Previous (←)">‹ Prev</button>
          <button onClick={next} style={navBtn} title="Next (→)">Next ›</button>
          <button onClick={onClose} style={{ ...navBtn, background: "#dc2626", color: "#fff" }} title="Close (Esc)">Close</button>
        </div>

        <div style={{ overflow: "auto", border: "1px solid #e5e7eb", borderRadius: 6 }}>
          {t.snipImageBase64 ? (
            <img
              src={`data:image/png;base64,${t.snipImageBase64}`}
              alt={`Snip of ${t.title}`}
              style={{ display: "block", maxWidth: "85vw", maxHeight: "75vh" }}
            />
          ) : (
            <div style={{ padding: 40, color: "#6b7280" }}>(No snip available)</div>
          )}
        </div>
      </div>
    </div>
  );
};

const navBtn: React.CSSProperties = {
  padding: "6px 10px",
  borderRadius: 6,
  border: "1px solid #d1d5db",
  background: "#fff",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};

export default PdfExtractPanel;
